<template>
    <v-form @submit.prevent="startUpdate">
        <v-row>
            <v-col cols="12">
                <p class="text-body-2 text-medium-emphasis mb-2">
                    Upload an Excel file with ITS numbers in column A (one per row). The first row is always treated as a header and skipped.
                </p>
                <v-file-input
                    v-model="selectedFile"
                    label="Excel file"
                    accept=".xlsx,.xls"
                    prepend-icon="mdi-file-excel"
                    show-size
                    :disabled="isProcessing"
                />
            </v-col>
            <v-col cols="12">
                <v-autocomplete
                    label="New Status"
                    v-model="toStatusId"
                    :items="memberStatus"
                    item-title="description"
                    item-value="generalID"
                    :disabled="isProcessing"
                />
            </v-col>
            <v-col cols="12" v-if="preview">
                <v-alert type="info" variant="tonal" density="compact">
                    <div>Total rows: {{ preview.totalRows }}</div>
                    <div>Valid ITS: {{ preview.validItsIds?.length ?? 0 }}</div>
                    <div v-if="preview.notFound?.length">Not found: {{ preview.notFound.length }}</div>
                    <div v-if="preview.invalidRows?.length">Invalid rows: {{ preview.invalidRows.length }}</div>
                    <div v-if="preview.duplicateItsIds?.length">Duplicates skipped: {{ preview.duplicateItsIds.length }}</div>
                </v-alert>
            </v-col>
            <v-col cols="12" v-if="isProcessing || processedCount > 0">
                <div class="text-body-2 mb-1">{{ progressLabel }}</div>
                <v-progress-linear :model-value="progressPercent" color="primary" height="8" rounded />
            </v-col>
            <v-col cols="12" v-if="finalSummary">
                <v-alert type="success" variant="tonal" density="compact">
                    <div>Updated: {{ finalSummary.updated }}</div>
                    <div>Skipped: {{ finalSummary.skipped }}</div>
                    <div>Not found: {{ finalSummary.notFound }}</div>
                    <div v-if="finalSummary.issueCount > 0">Issues report downloaded ({{ finalSummary.issueCount }} rows)</div>
                </v-alert>
            </v-col>
        </v-row>
        <div class="d-flex justify-end mt-2">
            <v-btn variant="text" class="mr-2" @click="$emit('onClose')" :disabled="isProcessing">Close</v-btn>
            <v-btn
                v-if="!preview"
                color="primary"
                @click="loadPreview"
                :disabled="!canPreview || isProcessing"
                :loading="isPreviewLoading"
            >
                Preview
            </v-btn>
            <v-btn
                v-else
                color="primary"
                type="submit"
                :disabled="!canUpdate || isProcessing"
                :loading="isProcessing"
            >
                Apply Update
            </v-btn>
        </div>
    </v-form>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import moment from 'moment';
import * as utService from '@/services/utilitiesService';
import * as mrService from '@/services/memberRequestService';

const emit = defineEmits(['onSave', 'onClose']);

const selectedFile = ref(null);
const toStatusId = ref(null);
const memberStatus = ref([]);
const preview = ref(null);
const isPreviewLoading = ref(false);
const isProcessing = ref(false);
const processedCount = ref(0);
const totalToProcess = ref(0);
const finalSummary = ref(null);

const canPreview = computed(() => {
    const file = Array.isArray(selectedFile.value) ? selectedFile.value[0] : selectedFile.value;
    return !!file;
});

const canUpdate = computed(() => {
    return !!preview.value?.validItsIds?.length && !!toStatusId.value;
});

const progressPercent = computed(() => {
    if (totalToProcess.value === 0)
        return 0;
    return Math.round((processedCount.value / totalToProcess.value) * 100);
});

const progressLabel = computed(() => {
    if (totalToProcess.value === 0)
        return '';
    return `Processing ${processedCount.value} of ${totalToProcess.value}`;
});

function getFileFromInput() {
    return Array.isArray(selectedFile.value) ? selectedFile.value[0] : selectedFile.value;
}

function downloadBase64Excel(base64, filename) {
    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    const url = URL.createObjectURL(new Blob([bytes], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

async function loadPreview() {
    const file = getFileFromInput();
    if (!file)
        return;

    isPreviewLoading.value = true;
    finalSummary.value = null;
    processedCount.value = 0;
    totalToProcess.value = 0;

    try {
        preview.value = await mrService.status_bulkPreview(file);
    } catch (ex) {
        preview.value = null;
        console.error(ex);
    } finally {
        isPreviewLoading.value = false;
    }
}

async function startUpdate() {
    if (!canUpdate.value)
        return;

    const file = getFileFromInput();
    if (!file)
        return;

    isProcessing.value = true;
    finalSummary.value = null;
    processedCount.value = 0;
    totalToProcess.value = preview.value.validItsIds.length;

    try {
        const result = await mrService.status_bulkApply(file, toStatusId.value);

        finalSummary.value = {
            updated: result.updated ?? 0,
            skipped: result.skipped ?? 0,
            notFound: result.notFound ?? 0,
            issueCount: result.issueCount ?? 0,
        };

        if (result.issuesExcelBase64) {
            const filename = `bulk_status_issues_${moment().format('DDMMMYYYY_HHmm')}.xlsx`;
            downloadBase64Excel(result.issuesExcelBase64, filename);
        }

        processedCount.value = totalToProcess.value;
        emit('onSave');
    } catch (ex) {
        console.error(ex);
    } finally {
        isProcessing.value = false;
    }
}

onMounted(async () => {
    memberStatus.value = await utService.getMemberStatus().catch(() => []);
});
</script>
